import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { BraintreeInstances, VenmoPaymentResponse } from '@/types/venmo';
import { VENMO_ERRORS } from '@/constants/venmo';
import { CheckoutContext } from '@/context/CheckoutContext';
import { braintreeService } from '@/services/braintreeService';
import { useTranslate } from '@/hooks/useTranslate';

interface UseVenmoBraintreeParams {
  braintreePK?: string;
  onPaymentError?: (error: string) => void;
}

export const useVenmoBraintree = ({ braintreePK, onPaymentError }: UseVenmoBraintreeParams) => {
  const [instances, setInstances] = useState<BraintreeInstances>({
    venmo: null,
    dataCollector: null,
  });
  const [isInitializing, setIsInitializing] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const isPaymentInProgress = useRef(false);

  const {
    actions: { runPayment, setError },
  } = useContext(CheckoutContext);
  const { t } = useTranslate();

  const initializeBraintree = useCallback(
    async (publicKey: string) => {
      setIsInitializing(true);
      try {
        const [venmo, dataCollector] = await Promise.all([
          braintreeService.createVenmoClient(publicKey),
          braintreeService.createBraintreeDataCollector(publicKey),
        ]);

        setInstances({ venmo, dataCollector });
      } catch {
        setError(VENMO_ERRORS.INITIALIZATION_FAILED);
        setInstances({ venmo: null, dataCollector: null });
      } finally {
        setIsInitializing(false);
      }
    },
    [setError]
  );

  useEffect(() => {
    if (braintreePK) {
      initializeBraintree(braintreePK);
    }
  }, [braintreePK, initializeBraintree]);

  const processPayment = useCallback(
    async (nonce: string) => {
      await runPayment<VenmoPaymentResponse>({
        nonce,
        deviceData: instances.dataCollector?.deviceData,
      });
    },
    [instances.dataCollector, runPayment]
  );

  const handleSubmit = useCallback(async () => {
    if (isPaymentInProgress.current || isProcessing) {
      return;
    }

    if (!instances.venmo) {
      setError(t('somethingWentWrong'));
      return;
    }

    const isSupported = instances.venmo.isBrowserSupported();

    if (!isSupported) {
      setError(t('paymentMethodBrowserNotSupported'));
      return;
    }

    setError('');
    setIsProcessing(true);
    isPaymentInProgress.current = true;

    try {
      const payload = await instances.venmo.tokenize();
      const nonce = payload.nonce;

      await processPayment(nonce);
    } catch (error) {
      const braintreeError = error as { code?: string; message?: string };

      if (braintreeError?.code === 'VENMO_CANCELED' || braintreeError?.code === 'VENMO_APP_CANCELED') {
        setError(t('somethingWentWrong'));
      } else {
        const errorMessage = error instanceof Error ? error.message : t('somethingWentWrong');
        setError(errorMessage);
      }
      onPaymentError?.(braintreeError?.message || t('somethingWentWrong'));
    } finally {
      setIsProcessing(false);
      isPaymentInProgress.current = false;
    }
  }, [isProcessing, instances.venmo, processPayment, setError, onPaymentError]);

  return {
    venmoInstance: instances.venmo,
    dataCollector: instances.dataCollector,
    isInitializing,
    isProcessing,
    isReady: !isInitializing && !!instances.venmo,
    handleSubmit,
  };
};
